import { FinalInvoiceStatus } from '@arhs-fleetback/types/entities/interventions';
import { createSelector } from 'reselect';

import { DISPLAY_NAME_FORMATTERS } from 'components/DisplayNameFormatter/useDisplayNameFormatter';
import { PROJECTION } from 'constants/ApiConstants';
import { CHECKLIST_SELECT_LABEL } from 'constants/ChecklistConstants';
import * as FILTERS from 'constants/FilterConstants';
import { PaginationType } from 'constants/Global';
import { INTERVENTION_MODE, DEFAULT_STATUS, DATE_TYPE, DELETED, OPEN, IN_PROGRESS, STATUSES } from 'constants/InterventionConstants';

import { getLocaleDetails } from 'constants/LanguageConstants';
import { PARTS, TECHNICIAN } from 'constants/ModeConstants';
import {
  PARTS_REMINDER,
  REMINDER,
  BODYWORK_APPOINTMENT,
  PARTS as PARTS_MODULE,
  DEFAULT_TECHNICIAN_MODULES,
} from 'constants/ModuleConstants';
import { PaymentStatus, SUCCEED, DEFAULT_PAYMENT_STATUS } from 'constants/PaymentConstants';
import {
  CHECK_IN_STATUS,
  CHECK_OUT_STATUS,
  DASHBOARD_FILTERS,
  SELF_SERVICE_STATUS,
  INCLUDE_MANUAL_CHECK_OUT,
} from 'constants/SelfServiceConstants';
import { FIXED_STEP_NAME } from 'constants/StepsConstants';
import { getCountries, resolveIANATimezone } from 'modules/countries/selectors';
import { StepsTypeEnum } from 'modules/global/typing/enums/steps.enum';
import { getLocale } from 'modules/i18n/selectors/data';
import { getAll as getAllSteps } from 'modules/steps/selectors/details';
import { getDataById } from 'modules/userGroups/selectors';
import {
  getCurrentUserIsAdmin,
  getCurrentUserIsGroupManager,
} from 'modules/users/selectors/currentUser';
import {
  getDealersEntitiesById,
  getSelectedDealerId,
  getSelectedDealerTimeZoneOffset,
} from 'selectors/dealerSelectors';
import { humanize, ucFirst } from 'utils/global';
import { createDictionary, omit } from 'utils/ObjectUtils';
import { getProps } from 'utils/selectors';
import { getDayDates, getWeekDates } from 'utils/selectors/formatter';

import { getDataEntities, getState, getUIState } from './base';
import { getInterventionApiError } from './details';

export const getDashboard = createSelector(getUIState, ui => ui.dashboard);

export const getDashboardMode = createSelector(getDashboard, dashboard => dashboard.mode);
export const getUIDashboardModule = createSelector(getDashboard, dashboard => dashboard.module);

export const getDashboardLoading = createSelector(getDashboard, dashboard => dashboard.loading);
export const getDashboardIds = createSelector(getDashboard, dashboard => dashboard.ids);
export const getDashboardPage = createSelector(getDashboard, dashboard => dashboard.page);

const getPage = createSelector(getDashboard, dashboard => dashboard.page);
export const hasNext = createSelector(getDashboard, dashboard => dashboard.hasMore);

export const getApiError = createSelector(getDashboard, dashboard => dashboard.apiError);

export const getInterventionsApiError = createSelector(
  getApiError,
  getInterventionApiError,
  (dashboardError, detailsError) => dashboardError || detailsError
);

export const getUIDashboardInterventionMode = createSelector(getDashboardMode, mode => {
  if (mode === REMINDER) {
    return INTERVENTION_MODE.TECHNICIAN;
  }
  if (mode === PARTS_REMINDER) {
    return INTERVENTION_MODE.PARTS;
  }
  return mode;
});

export const isReminderDashboard = createSelector(
  getDashboardMode,
  mode => [REMINDER, PARTS_REMINDER].includes(mode)
);

export const getInterventionsItems = createSelector(
  getDashboardIds,
  getDataEntities,
  (ids, interventions) => ids?.map(id => interventions[id]).filter(Boolean) ?? []
);

export const getDashboardDealerTimezonesByDealerId = createSelector(
  getInterventionsItems,
  getDealersEntitiesById,
  getCountries,
  (interventions, dealersById, countries) =>
    interventions.reduce((acc, intervention) => {
      const dealerId = intervention.dealer?.id;
      if (dealerId && !acc[dealerId]) {
        const dealer = dealersById[dealerId];
        const rawTz = dealer?.timezone ?? dealer?.config?.general.timezone;
        const tz = resolveIANATimezone(countries, rawTz);
        if (tz) {
          acc[dealerId] = tz;
        }
      }
      return acc;
    }, {})
);

export const getAllInterventionsFilters = createSelector(getState, state => state.ui.filters);

export const getInterventionsFilters = createSelector(
  getAllInterventionsFilters,
  getAllSteps,
  getDashboardMode,
  getUIDashboardModule,
  getSelectedDealerId,
  (filters, steps, mode, module, dealerId) => {
    const stepIds = Object.keys(steps);
    const hasSelfService = dealerId && module !== BODYWORK_APPOINTMENT;
    return {
      ...filters[mode],
      [FILTERS.STEPS]: filters[mode]?.[FILTERS.STEPS]?.filter(stepId => stepIds.includes(stepId)),
      // Clear self-service filter fields, avoid the case of the dealer hasn't available feature/extension
      [CHECK_IN_STATUS]: hasSelfService ? filters[mode]?.[CHECK_IN_STATUS] ?? [] : [],
      [CHECK_OUT_STATUS]: hasSelfService ? filters[mode]?.[CHECK_OUT_STATUS] ?? [] : [],
      [INCLUDE_MANUAL_CHECK_OUT]: hasSelfService ? filters[mode]?.[INCLUDE_MANUAL_CHECK_OUT] ?? false : false,
    };
  }
);

export const getModulesFilters = createSelector(
  getInterventionsFilters,
  getUIDashboardModule,
  getDashboardMode,
  ({ [FILTERS.MODULES]: modules }, module, mode) => {
    if (module === BODYWORK_APPOINTMENT) {
      return [BODYWORK_APPOINTMENT];
    }
    if (mode === PARTS) { // Assuming PARTS mode implies PARTS_MODULE
      return [PARTS_MODULE];
    }
    if (mode === TECHNICIAN && modules?.length === 0) {
      return DEFAULT_TECHNICIAN_MODULES;
    }
    return modules;
  }
);

const getAuthorsFilters = createSelector(getInterventionsFilters, filters => filters[FILTERS.AUTHORS]);
const getSendersFilters = createSelector(getInterventionsFilters, filters => filters[FILTERS.SENDERS]);
const getCheckInSendersFilters = createSelector(getInterventionsFilters, filters => filters[FILTERS.CHECK_IN_SENDERS]);
const getCheckOutSendersFilters = createSelector(getInterventionsFilters, filters => filters[FILTERS.CHECK_OUT_SENDERS]);
const getAnySendersFilters = createSelector(getInterventionsFilters, filters => filters[FILTERS.ANY_SENDERS]);
export const getSenderFilterMode = createSelector(getInterventionsFilters, filters => filters.senderFilterMode ?? FILTERS.SENDER_FILTER_MODE.EXACT);
export const getBrandsFilters = createSelector(getInterventionsFilters, filters => filters[FILTERS.BRANDS]);
export const getTagsFilters = createSelector(getInterventionsFilters, filters => filters[FILTERS.TAGS]);
const getRatingsFilters = createSelector(getInterventionsFilters, filters => filters[FILTERS.RATINGS]);
const getStepsFilters = createSelector(getInterventionsFilters, filters => filters[FILTERS.STEPS]);
const getStatusFilters = createSelector(getInterventionsFilters, filters => filters[FILTERS.STATUS]);
const getInterventionPaymentStatusFilters = createSelector(
  getInterventionsFilters,
  filters => filters[FILTERS.INTERVENTION_PAYMENT_STATUS]
);
const getAssignedUserFilters = createSelector(getInterventionsFilters, filters => filters[FILTERS.ASSIGNEE_USERS]);
const getAssignedUserGroupsFilters = createSelector(
  getInterventionsFilters,
  filters => filters[FILTERS.ASSIGNEE_USER_GROUPS]
);
const getAdvisorUserFilters = createSelector(getInterventionsFilters, filters => filters[FILTERS.ADVISOR_USERS]);
const getAdvisorUserGroupsFilters = createSelector(
  getInterventionsFilters,
  filters => filters[FILTERS.ADVISOR_USER_GROUPS]
);
const getNotExist = createSelector(getInterventionsFilters, filters => filters[FILTERS.NOT_EXIST]);
const isNotAssigned = createSelector(getNotExist, notExist => notExist.includes(FILTERS.ASSIGNEE));
const isNotStepped = createSelector(getNotExist, notExist => notExist.includes(FILTERS.STEPS));
export const hasNoAdvisor = createSelector(getNotExist, notExist => notExist.includes(FILTERS.ADVISOR));

const formatAuthors = (intl, authors = []) =>
  authors.length > 0 && {
    id: FILTERS.AUTHORS,
    name: intl.formatMessage({ id: 'page.home.filters.type.author', defaultMessage: 'Authors' }),
    values: authors.map(author => ({
      value: author.id,
      name: author.displayName,
      className: 'default',
    })),
  };
const getFormattedAuthors = createSelector(getProps, getAuthorsFilters, formatAuthors);

const getFormattedSenders = createSelector(
  getProps,
  getLocale,
  getSendersFilters,
  (intl, locale, senders = []) =>
    senders.length > 0 && {
      id: FILTERS.SENDERS,
      name: intl.formatMessage({ id: 'page.home.filters.type.filesSentBy', defaultMessage: 'Files sent by' }),
      values: senders.map(sender => ({
        value: sender.id,
        name: DISPLAY_NAME_FORMATTERS[getLocaleDetails(locale).country](sender),
      })),
    }
);

const getFormattedCheckInSenders = createSelector(
  getProps,
  getLocale,
  getCheckInSendersFilters,
  (intl, locale, checkInSenders = []) =>
    checkInSenders.length > 0 && {
      id: FILTERS.CHECK_IN_SENDERS,
      name: intl.formatMessage({ id: 'page.home.filters.type.checkInSentBy', defaultMessage: 'Check-in sent by' }),
      values: checkInSenders.map(checkInSender => ({
        value: checkInSender.id,
        name: DISPLAY_NAME_FORMATTERS[getLocaleDetails(locale).country](checkInSender),
      })),
    }
);

const getFormattedCheckOutSenders = createSelector(
  getProps,
  getLocale,
  getCheckOutSendersFilters,
  (intl, locale, checkOutSenders = []) =>
    checkOutSenders.length > 0 && {
      id: FILTERS.CHECK_OUT_SENDERS,
      name: intl.formatMessage({ id: 'page.home.filters.type.checkOutSentBy', defaultMessage: 'Check-out sent by' }),
      values: checkOutSenders.map(checkOutSender => ({
        value: checkOutSender.id,
        name: DISPLAY_NAME_FORMATTERS[getLocaleDetails(locale).country](checkOutSender),
      })),
    }
);

const getFormattedAnySenders = createSelector(
  getProps,
  getLocale,
  getAnySendersFilters,
  (intl, locale, anySenders = []) =>
    anySenders.length > 0 && {
      id: FILTERS.ANY_SENDERS,
      name: intl.formatMessage({ id: 'page.home.filters.type.senders', defaultMessage: 'Senders' }),
      values: anySenders.map(sender => ({
        value: sender.id,
        name: DISPLAY_NAME_FORMATTERS[getLocaleDetails(locale).country](sender),
      })),
    }
);

const formatBrands = (intl, brands = []) =>
  brands.length > 0 && {
    id: FILTERS.BRANDS,
    name: intl.formatMessage({
      id: 'page.home.filters.type.brand',
      defaultMessage: 'Brands'
    }),
    values: brands.map(brand => ({
      value: brand.id,
      name: brand.name,
    })),
  };

const formatTags = (intl, tags = []) =>
  tags.length > 0 && {
    id: FILTERS.TAGS,
    values: tags.map(tag => ({
      value: tag.id,
      name: tag.name,
    })),
    name: intl.formatMessage({ id: 'tags.title', defaultMessage: 'Tags' }),
  };

const getFormattedBrands = createSelector(getProps, getBrandsFilters, (intl, brands) => formatBrands(intl, brands));

const getFormattedTags = createSelector(getProps, getTagsFilters, (intl, tags) => formatTags(intl, tags));

const getFormattedStatus = createSelector(
  getProps,
  getStatusFilters,
  getInterventionPaymentStatusFilters,
  (intl, statuses = [], paymentStatuses = []) =>
    (statuses.length > 0 || paymentStatuses.length > 0) && {
      id: FILTERS.STATUS,
      name: intl.formatMessage({ id: 'page.home.filters.type.status', defaultMessage: 'Status' }),
      values: [
        ...statuses.map(status => ({
          value: status,
          name: intl.formatMessage({ id: `intervention_status.${status.toLowerCase()}`, defaultMessage: status }),
        })),
        ...paymentStatuses.map(status => ({
          value: status,
          name: intl.formatMessage({
            id: `page.home.filters.intervention_payment_status_filters.${status}`,
            defaultMessage: status,
          }),
          filterId: FILTERS.INTERVENTION_PAYMENT_STATUS,
        })),
      ],
    }
);

export const formatSteps = (intl, byId, noStepped, stepIds = []) => {
  const hasValue = stepIds.length > 0 || noStepped;
  const noStepFormatted = noStepped
    ? [
      {
        value: FILTERS.STEPS,
        name: intl.formatMessage(FILTERS.NOT_EXIST_LABEL_DESCRIPTOR[FILTERS.STEPS]),
        filterId: FILTERS.NOT_EXIST,
      },
    ]
    : [];
  const stepIdsFormatted = stepIds.map(stepId => {
    const step = byId[stepId];
    const isFixed = step.type === StepsTypeEnum.FIXED;
    return {
      value: stepId,
      name: isFixed ? intl.formatMessage(FIXED_STEP_NAME[step.code]) : step.name,
    };
  });
  return (
    hasValue && {
      id: FILTERS.STEPS,
      name: intl.formatMessage({ id: 'page.home.filters.type.steps', defaultMessage: 'Steps' }),
      values: [...noStepFormatted, ...stepIdsFormatted],
    }
  );
};
const getFormattedSteps = createSelector(getProps, getAllSteps, isNotStepped, getStepsFilters, formatSteps);

const formatModules = (intl, filters, id) => {
  if (filters?.length > 0) {
    return {
      id,
      name: intl.formatMessage({ id: 'page.home.filters.type.modules', defaultMessage: 'Modules' }),
      values: [
        ...filters.map(filter => ({
          value: filter,
          name: intl.formatMessage({ id: filter.toLowerCase(), defaultMessage: humanize(filter) }),
        })),
      ],
    };
  }
  return undefined;
};
const getFormattedModules = createSelector(
  getProps,
  getInterventionsFilters,
  getUIDashboardModule,
  (intl, filters, module) =>
    (module !== BODYWORK_APPOINTMENT ? formatModules(intl, filters[FILTERS.MODULES], FILTERS.MODULES) : undefined)
);

export const formatAssigned = (intl, userGroupsById, userGroupIds = [], assignedUsers = [], notAssigned = false) => {
  const hasUsers = assignedUsers?.length > 0;
  const hasUserGroups = userGroupIds?.length > 0;
  return (
    (hasUsers || hasUserGroups || notAssigned) && {
      id: FILTERS.ASSIGNEE_USERS,
      name: intl.formatMessage({ id: 'page.home.filters.type.assignee', defaultMessage: 'Assignees' }),
      values: [
        ...(notAssigned
          ? [
            {
              value: FILTERS.ASSIGNEE,
              name: intl.formatMessage(FILTERS.NOT_EXIST_LABEL_DESCRIPTOR[FILTERS.ASSIGNEE]),
              filterId: FILTERS.NOT_EXIST,
            },
          ]
          : []),
        ...assignedUsers.map(user => ({
          value: user?.id,
          name: user?.displayName,
        })),
        ...userGroupIds.map(userGroupId => ({
          value: userGroupsById[userGroupId]?.id,
          name: userGroupsById[userGroupId]?.name,
          filterId: FILTERS.ASSIGNEE_USER_GROUPS,
        })),
      ],
    }
  );
};
export const formatAdvisor = (intl, userGroupsById, userGroupIds, assignedUsers, notAssigned) => {
  const formatted = formatAssigned(intl, userGroupsById, userGroupIds, assignedUsers, notAssigned);
  return typeof formatted === 'object'
    ? {
      ...formatted,
      id: FILTERS.ADVISOR_USERS,
      name: intl.formatMessage({
        id: 'page.steps.details.customerDescription.tags.service_advisor',
        defaultMessage: 'Service advisor',
      }),
      values: formatted.values?.map(filter => ({
        ...filter,
        value: filter.filterId === FILTERS.NOT_EXIST ? FILTERS.ADVISOR : filter.value,
        ...(filter.filterId && {
          filterId: filter.filterId === FILTERS.ASSIGNEE_USER_GROUPS ? FILTERS.ADVISOR_USER_GROUPS : filter.filterId,
        }),
      })),
    }
    : formatted;
};
const getFormattedAssigned = createSelector(
  getProps,
  getDataById,
  getAssignedUserGroupsFilters,
  getAssignedUserFilters,
  isNotAssigned,
  formatAssigned
);
const getFormattedAdvisors = createSelector(
  getProps,
  getDataById,
  getAdvisorUserGroupsFilters,
  getAdvisorUserFilters,
  hasNoAdvisor,
  formatAdvisor
);

const getFormattedChecklists = createSelector(
  getProps,
  getDashboardMode,
  getInterventionsFilters,
  (intl, mode, { [FILTERS.CHECKLISTS]: checklists }) =>
    checklists?.length > 0 && {
      id: FILTERS.CHECKLISTS,
      name: intl.formatMessage({ id: CHECKLIST_SELECT_LABEL[mode] }),
      values: checklists.map(checklist => ({
        value: checklist.id,
        name: checklist.name,
      })),
    }
);

const getFormattedRatings = createSelector(
  getProps,
  getRatingsFilters,
  (intl, ratings = []) =>
    ratings.length > 0 && {
      id: FILTERS.RATINGS,
      name: intl.formatMessage({ id: 'page.home.filters.type.rating', defaultMessage: 'Ratings' }),
      values: [
        ...ratings.map(rating => ({
          value: rating,
          name: intl.formatMessage(
            {
              id: 'page.fiche.rating.star',
              defaultMessage: '{count, plural, one {# star} other {# stars}}',
            },
            { count: rating }
          ),
        })),
      ],
    }
);

const getFormattedPendingAdvices = createSelector(
  getProps,
  getInterventionsFilters,
  (intl, { pendingAdvices }) =>
    pendingAdvices && {
      id: FILTERS.PENDING_ADVICES,
      name: intl.formatMessage({ id: 'page.home.filters.type.pendingAdvices', defaultMessage: 'Pending items' }),
      values: [
        {
          value: pendingAdvices,
          name: intl.formatMessage({ id: 'yes', defaultMessage: 'Yes' }),
        },
      ],
    }
);

const getFormattedSelfServiceStatus = createSelector(
  getProps,
  getInterventionsFilters,
  (
    intl,
    {
      [CHECK_IN_STATUS]: checkInStatus,
      [CHECK_OUT_STATUS]: checkOutStatus,
      [INCLUDE_MANUAL_CHECK_OUT]: includeManualCheckout,
    }
  ) =>
    (checkInStatus?.length > 0 || checkOutStatus?.length > 0 || includeManualCheckout) && {
      id: FILTERS.SELF_SERVICE_STATUS,
      name: intl.formatMessage({
        id: 'page.home.filters.type.selfServiceStatus',
        defaultMessage: 'Self-service status',
      }),
      values: DASHBOARD_FILTERS.reduce((acc, { label, value }) => {
        const [type, status] = value;
        const listToUse = type === CHECK_IN_STATUS ? checkInStatus : checkOutStatus;
        if ((type === INCLUDE_MANUAL_CHECK_OUT && includeManualCheckout) || listToUse?.includes(status)) {
          acc.push({
            key: `${type}_${status}`,
            value: status === SELF_SERVICE_STATUS.SENT ? [SELF_SERVICE_STATUS.IN_PROGRESS, status] : status,
            name: intl.formatMessage({
              id: `intervention_self_service_status.${label}`,
              defaultMessage: ucFirst(humanize(label)),
            }),
            className: label,
            filterId: type,
          });
        }
        return acc;
      }, []),
    }
);

export const getFormattedFilters = createSelector(
  getFormattedModules,
  getFormattedAuthors,
  getFormattedSenders,
  getFormattedCheckInSenders,
  getFormattedCheckOutSenders,
  getFormattedAnySenders,
  getFormattedAssigned,
  getFormattedAdvisors,
  getFormattedBrands,
  getFormattedTags,
  getFormattedStatus,
  getFormattedRatings,
  getFormattedSteps,
  getFormattedChecklists,
  getFormattedPendingAdvices,
  getFormattedSelfServiceStatus,
  (
    modules,
    authors,
    senders,
    checkInSenders,
    checkOutSenders,
    anySenders,
    assigned,
    advisor,
    brands,
    tags,
    status,
    ratings,
    steps,
    checklists,
    pendingAdvices,
    selfServiceStatus
  ) => [
    ...(modules ? [modules] : []),
    ...(authors ? [authors] : []),
    ...(senders ? [senders] : []),
    ...(checkInSenders ? [checkInSenders] : []),
    ...(checkOutSenders ? [checkOutSenders] : []),
    ...(anySenders ? [anySenders] : []),
    ...(assigned ? [assigned] : []),
    ...(advisor ? [advisor] : []),
    ...(brands ? [brands] : []),
    ...(tags ? [tags] : []),
    ...(status ? [status] : []),
    ...(ratings ? [ratings] : []),
    ...(steps ? [steps] : []),
    ...(checklists ? [checklists] : []),
    ...(pendingAdvices ? [pendingAdvices] : []),
    ...(selfServiceStatus ? [selfServiceStatus] : []),
  ]
);

export const getNbFilters = createSelector(getFormattedFilters, filters =>
  filters.reduce((acc, { values = [] }) => acc + values.length, 0));

export const canFetchInterventions = createSelector(
  getCurrentUserIsGroupManager,
  getCurrentUserIsAdmin,
  getSelectedDealerId,
  (isGroupManager, isAdmin, dealerId) => {
    const isDealerMandatory = isAdmin || isGroupManager;
    return !isDealerMandatory || dealerId;
  }
);

const getStatusFilter = createSelector(getInterventionsFilters, filters => {
  const { displayClosed = false } = filters;
  const selectedStatuses = filters[FILTERS.STATUS];
  const hasSelectedStatuses = selectedStatuses?.length > 0;
  const statuses = hasSelectedStatuses ? selectedStatuses : DEFAULT_STATUS;
  if (!hasSelectedStatuses && !displayClosed) {
    return statuses.filter(status => status !== STATUSES.CLOSED);
  }
  return statuses;
});

const getSchedulableType = createSelector(getInterventionsFilters, filters => filters.schedulableType);

export const getSortType = createSelector(getInterventionsFilters, filters => filters.sortType);
export const getSortDirection = createSelector(getInterventionsFilters, filters => filters.sortDirection);
export const getSort = createSelector(getSortType, getSortDirection, (type, direction) => `${type},${direction}`);

export const getFormattedDashboardDateFilters = createSelector(
  getProps,
  getInterventionsFilters,
  getSchedulableType,
  getSortType,
  getDashboardMode,
  isReminderDashboard,
  getSelectedDealerTimeZoneOffset,
  (now, interventionFilters, schedulableType, sortType, mode, isReminder, timeZoneOffset) => {
    const { startDateFrom, startDateTo, endDateFrom, endDateTo, period } = interventionFilters;
    const isStartDate = sortType === 'startDate';

    const config = {
      [FILTERS.ALL]: { sendDateFrom: null, sendDateTo: null },
      [FILTERS.TODAY]: getDayDates(timeZoneOffset),
      [FILTERS.WEEK]: getWeekDates(timeZoneOffset),
      [FILTERS.CUSTOM]: {
        sendDateTo: null,
        sendDateFrom: startDateFrom,
      },
    };

    if (isReminder) {
      return config[period] ?? { from: null, to: null };
    }

    if (mode === TECHNICIAN) {
      if (schedulableType === DATE_TYPE.CURRENT) {
        return {
          startDateFrom: null,
          startDateTo: isStartDate ? now : null,
          endDateFrom: null,
          endDateTo: !isStartDate ? now : null,
        };
      }

      if (schedulableType === DATE_TYPE.PLANNED) {
        return {
          startDateFrom: isStartDate ? now : null,
          startDateTo: null,
          endDateFrom: !isStartDate ? now : null,
          endDateTo: null,
        };
      }

      return {
        startDateFrom,
        startDateTo,
        endDateFrom,
        endDateTo,
      };
    }

    // Sales case
    return {
      startDateFrom: startDateFrom ?? undefined,
      startDateTo: startDateTo ?? now,
    };
  }
);

const DEFAULT_FILTERS = createDictionary(
  {
    [REMINDER]: {
      modules: [REMINDER],
      projection: PROJECTION.REMINDERS,
      [FILTERS.MODE]: TECHNICIAN,
    },
    [PARTS_REMINDER]: {
      modules: [PARTS_REMINDER],
      projection: PROJECTION.REMINDERS,
      [FILTERS.MODE]: PARTS,
    },
  },
  {}
);

/**
 * Filters that getFormattedDashboardFilters maps, renames or drops explicitly below. Omitting them leaves
 * `cleanFilters` with only the ones forwarded to the API untouched.
 */
const EXPLICITLY_HANDLED_FILTERS = [
  FILTERS.PERIOD,
  FILTERS.STATUS,
  FILTERS.BRANDS,
  FILTERS.START_DATE_TO,
  FILTERS.START_DATE_FROM,
  FILTERS.SORT_DIRECTION,
  FILTERS.PAYMENT_PROGRESS_STATUS,
  FILTERS.SCHEDULABLE_TYPE,
  FILTERS.ASSIGNEE_USERS,
  FILTERS.ADVISOR_USERS,
  FILTERS.ADVISOR_USER_GROUPS,
  FILTERS.CHECKLISTS,
  FILTERS.NOT_EXIST,
  FILTERS.TAGS,
  FILTERS.ANY_SENDERS,
  // no constant in FilterConstants for these
  'sortType',
  'endDateTo',
  'endDateFrom',
  'senderFilterMode',
];

export const getFormattedDashboardFilters = createSelector(
  getInterventionsFilters,
  getSelectedDealerId,
  isReminderDashboard,
  getDashboardMode,
  getModulesFilters,
  getStatusFilter,
  getAssignedUserGroupsFilters,
  getFormattedDashboardDateFilters,
  getSort,
  (filters, dealerId, isReminder, mode, modules, statuses, userGroups, dateFilters, sort) => {
    const { paymentStatus, [FILTERS.NOT_EXIST]: notExist } = filters;
    const cleanFilters = omit(filters, ...EXPLICITLY_HANDLED_FILTERS);

    const commonFilters = {
      [FILTERS.STATUS]: statuses,
      [FILTERS.SELECTED_DEALER_IDS]: [dealerId],
      [FILTERS.SEARCH]: filters[FILTERS.SEARCH],
      [FILTERS.FOLLOWED]: filters[FILTERS.FOLLOWED],
      [FILTERS.BRANDS]: filters[FILTERS.BRANDS]?.map(item => item.id),
      [FILTERS.TAGS]: filters[FILTERS.TAGS]?.map(item => item.id),
      [FILTERS.CHECKLISTS]: filters[FILTERS.CHECKLISTS]?.map(item => item.id),
      [FILTERS.ADVISOR_USERS]: filters[FILTERS.ADVISOR_USERS]?.map(item => item.id),
      [FILTERS.ASSIGNEE_USERS]: filters[FILTERS.ASSIGNEE_USERS]?.map(item => item.id),
      ...dateFilters,
      sort,
    };

    if (isReminder) {
      return {
        ...commonFilters,
        ...DEFAULT_FILTERS[mode],
        [FILTERS.REMINDER_WITHOUT_PRICE]: filters[FILTERS.REMINDER_WITHOUT_PRICE],
      };
    }

    let paymentFilter = { paymentStatus };
    if (paymentStatus?.includes(SUCCEED)) {
      paymentFilter = {
        paymentStatus: [PaymentStatus.SUCCESS],
        finalInvoiceStatus: FinalInvoiceStatus.PAID,
      };
    }

    return {
      ...commonFilters,
      ...cleanFilters,
      ...paymentFilter,
      ...DEFAULT_FILTERS[mode],
      modules,
      [FILTERS.MODE]: mode,
      [FILTERS.ASSIGNEE_USER_GROUPS]: userGroups,
      [FILTERS.ADVISOR_USER_GROUPS]: filters[FILTERS.ADVISOR_USER_GROUPS],
      [FILTERS.AUTHORS]: filters[FILTERS.AUTHORS]?.map(item => item.id),
      ...(filters.senderFilterMode === FILTERS.SENDER_FILTER_MODE.ANY
        ? {
          [FILTERS.ANY_SENDERS]: filters[FILTERS.ANY_SENDERS]?.map(item => item.id),
        }
        : {
          [FILTERS.SENDERS]: filters[FILTERS.SENDERS]?.map(item => item.id),
          [FILTERS.CHECK_IN_SENDERS]: filters[FILTERS.CHECK_IN_SENDERS]?.map(item => item.id),
          [FILTERS.CHECK_OUT_SENDERS]: filters[FILTERS.CHECK_OUT_SENDERS]?.map(item => item.id),
        }),
      [FILTERS.NOT_EXIST]: [...FILTERS.formatNotExistRequestFilter(notExist), 'appointmentId'],
      projection: 'page',
      paginationType: PaginationType.SLICE,
    };
  }
);

export const getParams = createSelector(getFormattedDashboardFilters, getPage, (params, page) => ({
  ...params,
  page: page.number,
  size: 20,
}));

const getDashboardStatus = createSelector(getDashboard, dashboard => dashboard.status);

export const getStatusData = createSelector(
  getDashboardStatus,
  state => state.data
);
export const getStatusError = createSelector(
  getDashboardStatus,
  state => state.error
);
export const isStatusFetching = createSelector(
  getDashboardStatus,
  state => state.loading
);

export const getFormattedInterventionStatus = createSelector(
  getStatusData,
  isReminderDashboard,
  (statuses, isReminder) => {
    const excludedStatus = [DELETED, IN_PROGRESS];
    if (isReminder) {
      excludedStatus.push(OPEN);
    }
    return statuses.filter(statusValue => !excludedStatus.includes(statusValue));
  }
);

export const getFormattedInterventionPaymentStatus = createSelector(
  isReminderDashboard,
  isReminder => (isReminder ? null : DEFAULT_PAYMENT_STATUS)
);
